import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { RiSearchLine } from 'react-icons/ri'

interface ClientSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (selectedClients: number[]) => void
  clientOptions: { label: string; value: number }[]
  selectedClients: number[]
}

export function ClientSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  clientOptions,
  selectedClients,
}: ClientSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [tempSelectedClients, setTempSelectedClients] = useState<number[]>([])

  useEffect(() => {
    if (isOpen) {
      setTempSelectedClients(selectedClients)
      setSearchTerm('')
    }
  }, [isOpen, selectedClients])

  const filteredClients = clientOptions.filter((client) =>
    client.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleToggleClient = (clientId: number) => {
    setTempSelectedClients((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId],
    )
  }

  const handleSelectAll = () => {
    if (tempSelectedClients.length === filteredClients.length) {
      setTempSelectedClients([])
    } else {
      setTempSelectedClients(filteredClients.map((client) => client.value))
    }
  }

  const handleConfirm = () => {
    onConfirm(tempSelectedClients)
    onClose()
  }

  const handleCancel = () => {
    setTempSelectedClients(selectedClients)
    onClose()
  }

  const isAllSelected = filteredClients.length > 0 && tempSelectedClients.length === filteredClients.length
  const isSomeSelected = tempSelectedClients.length > 0 && tempSelectedClients.length < filteredClients.length

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" {...({} as any)}>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Selecionar Clientes
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Campo de busca */}
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2}>
                Buscar cliente
              </Text>
              <Input
                placeholder="Digite o nome do cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="gray.700"
                color="white"
                borderColor="gray.600"
                _hover={{ borderColor: 'pink.400' }}
                _focus={{ borderColor: 'pink.400', boxShadow: 'none' }}
                leftIcon={<RiSearchLine />}
                {...({} as any)}
              />
            </Box>

            {/* Selecionar todos */}
            {filteredClients.length > 0 && (
              <HStack
                p={2}
                bg="gray.700"
                borderRadius="md"
                cursor="pointer"
                onClick={handleSelectAll}
                _hover={{ bg: 'gray.600' }}
                {...({} as any)}>
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={isSomeSelected}
                  onChange={handleSelectAll}
                  colorScheme="pink"
                  {...({} as any)}
                />
                <Text fontSize="sm" fontWeight="medium">
                  {isAllSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                </Text>
              </HStack>
            )}

            {/* Lista de clientes */}
            <Box maxHeight="300px" overflowY="auto">
              <VStack spacing={0} align="stretch">
                {filteredClients.length === 0 ? (
                  <Text color="gray.400" textAlign="center" py={4}>
                    {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente disponível'}
                  </Text>
                ) : (
                  filteredClients.map((client) => (
                    <HStack
                      key={client.value}
                      p={3}
                      _hover={{ bg: 'gray.600' }}
                      cursor="pointer"
                      onClick={() => handleToggleClient(client.value)}
                      borderRadius="md"
                      {...({} as any)}>
                      <Checkbox
                        isChecked={tempSelectedClients.includes(client.value)}
                        onChange={() => handleToggleClient(client.value)}
                        colorScheme="pink"
                        {...({} as any)}
                      />
                      <Text color="white" fontSize="sm">
                        {client.label}
                      </Text>
                    </HStack>
                  ))
                )}
              </VStack>
            </Box>

            {/* Contador de selecionados */}
            {tempSelectedClients.length > 0 && (
              <Text fontSize="sm" color="pink.400" textAlign="center">
                {tempSelectedClients.length} cliente(s) selecionado(s)
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Flex gap={2} width="100%">
            <Button variant="ghost" onClick={handleCancel} flex={1}>
              Cancelar
            </Button>
            <Button colorScheme="pink" onClick={handleConfirm} flex={1}>
              OK
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
